import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLogto } from '@logto/react';
import {
  TrendingUp,
  Settings,
  ArrowLeft,
  AlertCircle,
  DollarSign,
  BookOpen,
  Database,
  Activity,
  Calendar,
  Megaphone,
  BarChart3,
  LineChart,
  Zap,
  Lightbulb,
  Target,
  Wrench,
  Baby,
  Download,
  FileJson,
  FileSpreadsheet,
  Bot,
  FolderOpen,
  Check,
  X,
  Info,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  ThumbsUp,
  AlertTriangle
} from 'lucide-react';
import { SavedModelsBrowser } from './SavedModelsBrowser';
import { ChatPanel } from './ChatPanel';
import { SettingsModal } from './SettingsModal';
import { VisualizationDashboard } from './Visualizations';
import { ScenarioPlannerModal } from './ScenarioPlannerModal';
import { DiagnosticAssistantPanel } from './DiagnosticAssistantPanel';
import { ModelMetadata, modelService } from '../services/modelService';
import { apiGet } from '../utils/api';
import { API_ENDPOINTS } from '../config/apiConfig';
import {
  assessNRMSE,
  assessR2,
  assessDecompRSSD,
  assessDatasetSize,
  assessOverallQuality,
} from '../utils/metricQuality';

interface ResultsViewProps {
  jobId: string;
  onBack?: () => void;
}

interface AnalysisResults {
  metrics: {
    nrmse_train: number;
    nrmse_val: number;
    r2_train: number;
    r2_val: number;
    adj_r2_train: number;
    adj_r2_val: number;
    nrmse_test?: number;
    r2_test?: number;
    adj_r2_test?: number;
    decomp_rssd_val?: number | null;  // Business error: spend-attribution alignment (null = computation failed)
    decomp_rssd_status?: string;  // Status: success, failed_*, pending
    n_obs: number;
    n_params: number;
  };
  coefficients: Record<string, number>;
  contributions: Record<string, { coefficient: number; elasticity: number }>;
  best_params: Record<string, number>;
  config?: {
    dep_var?: string;
    dep_var_label?: string;
    date_var?: string;
    frequency?: string;
    calendar?: {
      use_trend?: boolean;
      use_seasonality?: boolean;
      seasonality_periods?: number[];
    };
    train_test_split?: {
      type?: string;
      train_ratio?: number;
      val_ratio?: number;
      test_ratio?: number;
    };
    media_channels?: Array<{
      name: string;
      column: string;
      variable_column?: string | null;
      adstock?: string;
      saturation?: string;
    }>;
    control_vars?: Array<{
      name: string;
      column: string;
    }>;
    optimization?: {
      budget?: number;
    };
  };
  // Visualization data for AI analyst chat context
  actual_vs_predicted?: any[];
  adstock_decomposition?: any[];
  decay_curves?: any[];
  response_curves?: any[];
  spend_share?: any[];
  performance_metrics?: any[];
  media_channels?: any[];
  bootstrapped_metrics?: any[];
}

export const ResultsView: React.FC<ResultsViewProps> = ({ jobId, onBack }) => {
  const [results, setResults] = useState<AnalysisResults | null>(null);
  const [visualizationData, setVisualizationData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSavedModels, setShowSavedModels] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [showScenarioPlanner, setShowScenarioPlanner] = useState(false);
  const [savedModels, setSavedModels] = useState<ModelMetadata[]>([]);
  const [chatQuestion, setChatQuestion] = useState<string | undefined>(undefined);
  const logto = useLogto();
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [showModelConfig, setShowModelConfig] = useState(false);

  // Ref for tracking component mount state and aborting requests
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Track mount state
    isMountedRef.current = true;

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const fetchResults = async () => {
      try {
        setLoading(true);
        setError(null);

        // Step 1: Fetch job status to get the model_id
        const statusData = await apiGet<any>(API_ENDPOINTS.ANALYSIS.STATUS(jobId), logto);

        // Check if request was aborted or component unmounted
        if (!isMountedRef.current) return;

        // Extract model_id from job results
        const modelId = statusData.results?.model_id;
        if (!modelId) {
          throw new Error('No model ID found in job results');
        }

        // Step 2: Fetch visualization data using the model_id
        const data = await apiGet<any>(API_ENDPOINTS.MODEL.VISUALIZATION_DATA(modelId), logto);

        // Final check before state updates
        if (!isMountedRef.current) return;

        // Save the raw visualization data for the dashboard
        setVisualizationData(data);

        // Map the visualization data to AnalysisResults format
        // Add defensive null checks for all nested properties
        const analysisResults: AnalysisResults = {
          metrics: {
            nrmse_train: data?.metrics?.nrmse_train ?? 0,
            nrmse_val: data?.metrics?.nrmse_val ?? 0,
            r2_train: data?.metrics?.r2_train ?? 0,
            r2_val: data?.metrics?.r2_val ?? 0,
            adj_r2_train: data?.metrics?.adj_r2_train ?? 0,
            adj_r2_val: data?.metrics?.adj_r2_val ?? 0,
            nrmse_test: data?.metrics?.nrmse_test,
            r2_test: data?.metrics?.r2_test,
            adj_r2_test: data?.metrics?.adj_r2_test,
            // Preserve null to distinguish "computation failed" from "0.0 = perfect alignment"
            decomp_rssd_val: data?.metrics?.decomp_rssd_val ?? null,
            decomp_rssd_status: data?.metrics?.decomp_rssd_status ?? 'unknown',
            n_obs: data?.metrics?.n_obs ?? 0,
            n_params: data?.metrics?.n_params ?? 0,
          },
          coefficients: data?.coefficients && typeof data.coefficients === 'object' ? data.coefficients : {},
          contributions: data?.contributions_by_channel && typeof data.contributions_by_channel === 'object' ? data.contributions_by_channel : {},
          best_params: data?.best_params && typeof data.best_params === 'object' ? data.best_params : {},
          config: data?.config,
          // Include all visualization data for AI analyst chat context
          // Use defensive checks for all arrays
          actual_vs_predicted: Array.isArray(data?.actual_vs_predicted) ? data.actual_vs_predicted : [],
          adstock_decomposition: Array.isArray(data?.adstock_decomposition) ? data.adstock_decomposition : [],
          decay_curves: Array.isArray(data?.decay_curves) ? data.decay_curves : [],
          response_curves: Array.isArray(data?.response_curves) ? data.response_curves : [],
          spend_share: Array.isArray(data?.spend_share) ? data.spend_share : [],
          performance_metrics: data?.performance_metrics || {},
          media_channels: Array.isArray(data?.media_channels) ? data.media_channels : [],
          bootstrapped_metrics: data?.bootstrapped_metrics || {},
        };

        setResults(analysisResults);

        // Extract diagnostics if available
        if (data.diagnostics) {
          setDiagnostics({
            failure_mode: data.diagnostics.failure_mode,
            issues: data.diagnostics.issues || [],
            actionable_recommendations: data.diagnostics.actionable_recommendations || [],
            quality_score: data.metrics?.r2_val || 0
          });
        }
      } catch (err) {
        // Ignore abort errors - they're expected when component unmounts or jobId changes
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        if (!isMountedRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load results');
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchResults();

    // Cleanup on unmount or jobId change
    return () => {
      isMountedRef.current = false;
      abortController.abort();
    };
  }, [jobId]);

  // Load saved models for comparison
  useEffect(() => {
    const loadSavedModels = async () => {
      try {
        const models = await modelService.listModels(logto);
        setSavedModels(models);
      } catch (err) {
        console.error('Failed to load saved models:', err);
      }
    };

    loadSavedModels();
  }, []);

  const handleSelectModel = (_modelId: string, _modelMetadata: ModelMetadata) => {
    setShowSavedModels(false);
    // TODO: Load the selected model's results for comparison
    alert('Model loading from saved models coming soon!');
  };

  // Memoized helper to open chat with a specific question (prevents unnecessary re-renders)
  const openChatWithQuestion = useCallback((question: string) => {
    setChatQuestion(question);
    setChatOpen(true);
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="card p-8 text-center">
          <div className="inline-block mb-4">
            <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full pulse"></div>
          </div>
          <p className="text-gray-600 font-medium">Loading analysis results...</p>
          <p className="text-sm text-gray-500 mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-8 fade-in">
        <div className="alert alert-error mb-6 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <div>
            <p className="font-semibold">Error Loading Results</p>
            <p className="text-sm mt-2">{error}</p>
          </div>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className="btn btn-secondary"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Configuration
          </button>
        )}
      </div>
    );
  }

  if (!results) {
    return (
      <div className="card p-8 text-center">
        <p className="text-gray-600 font-medium">No results available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 fade-in">
      {/* Premium Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 md:p-10">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-violet-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-xl shadow-violet-500/30">
                <Activity className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                  Analysis Results
                </h1>
                <p className="text-slate-400 flex items-center gap-2 mt-1">
                  <span className="text-sm">Job ID:</span>
                  <code className="bg-white/10 px-3 py-1 rounded-lg text-xs font-mono text-slate-300">{jobId}</code>
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowScenarioPlanner(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-400 hover:to-orange-400 transition-all shadow-lg shadow-amber-500/25"
              title="Test different channel allocations"
            >
              <DollarSign className="w-4 h-4" />
              Scenario Planner
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-all border border-white/10"
              title="Configure OpenAI API key for chat"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2.5 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-all border border-white/10"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/25">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Model Performance</h2>
        </div>

        {/* Overall Quality Summary Banner */}
        {(() => {
          const overallQuality = assessOverallQuality(
            results.metrics.nrmse_val,
            results.metrics.r2_val,
            results.metrics.decomp_rssd_val
          );
          const OverallIcon = overallQuality.level === 'excellent' ? CheckCircle :
                             overallQuality.level === 'good' ? ThumbsUp :
                             overallQuality.level === 'fair' ? AlertCircle : AlertTriangle;
          return (
            <div
              className={`mb-8 p-5 rounded-2xl border-2 ${overallQuality.bgColor} ${overallQuality.borderColor} cursor-pointer hover:shadow-md transition-shadow`}
              onClick={() => openChatWithQuestion('Give me an overall assessment of my model quality and what I can do to improve it.')}
            >
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  overallQuality.level === 'excellent' ? 'bg-emerald-500' :
                  overallQuality.level === 'good' ? 'bg-blue-500' :
                  overallQuality.level === 'fair' ? 'bg-amber-500' : 'bg-red-500'
                } shadow-lg`}>
                  <OverallIcon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className={`text-xl font-bold ${overallQuality.color}`}>
                      {overallQuality.label}
                    </h3>
                    <div className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                      overallQuality.level === 'excellent' ? 'bg-emerald-200 text-emerald-800' :
                      overallQuality.level === 'good' ? 'bg-blue-200 text-blue-800' :
                      overallQuality.level === 'fair' ? 'bg-amber-200 text-amber-800' : 'bg-red-200 text-red-800'
                    }`}>
                      {overallQuality.level}
                    </div>
                  </div>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    {overallQuality.description}
                  </p>
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Click to ask the AI analyst for detailed recommendations
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* NRMSE Card */}
          {(() => {
            const nrmseQuality = assessNRMSE(results.metrics.nrmse_val);
            const QualityIcon = nrmseQuality.level === 'excellent' ? CheckCircle :
                               nrmseQuality.level === 'good' ? ThumbsUp :
                               nrmseQuality.level === 'fair' ? AlertCircle : AlertTriangle;
            return (
              <div
                className={`group relative p-6 rounded-2xl border-2 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${nrmseQuality.bgColor} ${nrmseQuality.borderColor}`}
                onClick={() => openChatWithQuestion('What does NRMSE mean and how good is mine?')}
                title="Click to ask about this metric"
              >
                <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Info className="w-4 h-4 text-slate-600" />
                </div>
                {/* Quality Badge */}
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold mb-3 ${nrmseQuality.bgColor} ${nrmseQuality.color} border ${nrmseQuality.borderColor}`}>
                  <QualityIcon className="w-3.5 h-3.5" />
                  {nrmseQuality.label}
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Prediction Error</p>
                <p className="text-3xl font-bold text-slate-900 mb-2">
                  {results.metrics.nrmse_val !== undefined ? (results.metrics.nrmse_val * 100).toFixed(1) : 'N/A'}%
                </p>
                {/* Progress Bar */}
                <div className="w-full bg-white/50 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      nrmseQuality.level === 'excellent' ? 'bg-emerald-500' :
                      nrmseQuality.level === 'good' ? 'bg-blue-500' :
                      nrmseQuality.level === 'fair' ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${nrmseQuality.percentage}%` }}
                  />
                </div>
                <p className={`text-sm font-medium ${nrmseQuality.color}`}>
                  {nrmseQuality.level === 'excellent' ? 'Highly accurate predictions' :
                   nrmseQuality.level === 'good' ? 'Reliable predictions' :
                   nrmseQuality.level === 'fair' ? 'Room for improvement' : 'Needs refinement'}
                </p>
              </div>
            );
          })()}

          {/* R² Card */}
          {(() => {
            const r2Quality = assessR2(results.metrics.r2_val);
            const QualityIcon = r2Quality.level === 'excellent' ? CheckCircle :
                               r2Quality.level === 'good' ? ThumbsUp :
                               r2Quality.level === 'fair' ? AlertCircle : AlertTriangle;
            return (
              <div
                className={`group relative p-6 rounded-2xl border-2 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${r2Quality.bgColor} ${r2Quality.borderColor}`}
                onClick={() => openChatWithQuestion('What is R² and what does my R² value tell me?')}
                title="Click to ask about this metric"
              >
                <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Info className="w-4 h-4 text-slate-600" />
                </div>
                {/* Quality Badge */}
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold mb-3 ${r2Quality.bgColor} ${r2Quality.color} border ${r2Quality.borderColor}`}>
                  <QualityIcon className="w-3.5 h-3.5" />
                  {r2Quality.label}
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Model Fit</p>
                <p className="text-3xl font-bold text-slate-900 mb-2">
                  {results.metrics.r2_val !== undefined ? `${(results.metrics.r2_val * 100).toFixed(0)}%` : 'N/A'}
                </p>
                {/* Progress Bar */}
                <div className="w-full bg-white/50 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      r2Quality.level === 'excellent' ? 'bg-emerald-500' :
                      r2Quality.level === 'good' ? 'bg-blue-500' :
                      r2Quality.level === 'fair' ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${r2Quality.percentage}%` }}
                  />
                </div>
                <p className={`text-sm font-medium ${r2Quality.color}`}>
                  {results.metrics.r2_val !== undefined
                    ? `Explains ${(results.metrics.r2_val * 100).toFixed(0)}% of revenue changes`
                    : 'Unable to calculate'}
                </p>
              </div>
            );
          })()}

          {/* Adj R² Card */}
          {(() => {
            const adjR2Quality = assessR2(results.metrics.adj_r2_val);
            const QualityIcon = adjR2Quality.level === 'excellent' ? CheckCircle :
                               adjR2Quality.level === 'good' ? ThumbsUp :
                               adjR2Quality.level === 'fair' ? AlertCircle : AlertTriangle;
            return (
              <div className={`p-6 rounded-2xl border-2 ${adjR2Quality.bgColor} ${adjR2Quality.borderColor}`}>
                {/* Quality Badge */}
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold mb-3 ${adjR2Quality.bgColor} ${adjR2Quality.color} border ${adjR2Quality.borderColor}`}>
                  <QualityIcon className="w-3.5 h-3.5" />
                  {adjR2Quality.label}
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Adjusted Model Fit</p>
                <p className="text-3xl font-bold text-slate-900 mb-2">
                  {results.metrics.adj_r2_val !== undefined ? `${(results.metrics.adj_r2_val * 100).toFixed(0)}%` : 'N/A'}
                </p>
                {/* Progress Bar */}
                <div className="w-full bg-white/50 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      adjR2Quality.level === 'excellent' ? 'bg-emerald-500' :
                      adjR2Quality.level === 'good' ? 'bg-blue-500' :
                      adjR2Quality.level === 'fair' ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${adjR2Quality.percentage}%` }}
                  />
                </div>
                <p className={`text-sm font-medium ${adjR2Quality.color}`}>
                  Accounts for model complexity
                </p>
              </div>
            );
          })()}

          {/* Dataset Size Card */}
          {(() => {
            const dataQuality = assessDatasetSize(results.metrics.n_obs, results.metrics.n_params);
            const QualityIcon = dataQuality.level === 'excellent' ? CheckCircle :
                               dataQuality.level === 'good' ? ThumbsUp :
                               dataQuality.level === 'fair' ? AlertCircle : AlertTriangle;
            const ratio = results.metrics.n_params !== 0 ? (results.metrics.n_obs / results.metrics.n_params).toFixed(0) : 'N/A';
            return (
              <div className={`p-6 rounded-2xl border-2 ${dataQuality.bgColor} ${dataQuality.borderColor}`}>
                {/* Quality Badge */}
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold mb-3 ${dataQuality.bgColor} ${dataQuality.color} border ${dataQuality.borderColor}`}>
                  <QualityIcon className="w-3.5 h-3.5" />
                  {dataQuality.label}
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Data Size</p>
                <p className="text-3xl font-bold text-slate-900 mb-2">
                  {results.metrics.n_obs} rows
                </p>
                {/* Progress Bar */}
                <div className="w-full bg-white/50 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      dataQuality.level === 'excellent' ? 'bg-emerald-500' :
                      dataQuality.level === 'good' ? 'bg-blue-500' :
                      dataQuality.level === 'fair' ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${dataQuality.percentage}%` }}
                  />
                </div>
                <p className={`text-sm font-medium ${dataQuality.color}`}>
                  {ratio}:1 data-to-parameter ratio
                </p>
              </div>
            );
          })()}

          {/* DECOMP.RSSD Card */}
          {(() => {
            const rssdQuality = assessDecompRSSD(results.metrics.decomp_rssd_val);
            const QualityIcon = rssdQuality.level === 'excellent' ? CheckCircle :
                               rssdQuality.level === 'good' ? ThumbsUp :
                               rssdQuality.level === 'fair' ? AlertCircle : AlertTriangle;
            const hasFailed = results.metrics.decomp_rssd_status?.startsWith('failed');
            return (
              <div className={`p-6 rounded-2xl border-2 ${hasFailed ? 'bg-gray-100 border-gray-300' : `${rssdQuality.bgColor} ${rssdQuality.borderColor}`}`}>
                {/* Quality Badge */}
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold mb-3 ${hasFailed ? 'bg-gray-200 text-gray-600 border-gray-400' : `${rssdQuality.bgColor} ${rssdQuality.color}`} border ${hasFailed ? 'border-gray-400' : rssdQuality.borderColor}`}>
                  <QualityIcon className="w-3.5 h-3.5" />
                  {hasFailed ? 'Unavailable' : rssdQuality.label}
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Business Alignment</p>
                <p className="text-3xl font-bold text-slate-900 mb-2">
                  {results.metrics.decomp_rssd_val !== null && results.metrics.decomp_rssd_val !== undefined
                    ? `${(results.metrics.decomp_rssd_val * 100).toFixed(1)}%`
                    : 'N/A'}
                </p>
                {/* Progress Bar */}
                <div className="w-full bg-white/50 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      hasFailed ? 'bg-gray-400' :
                      rssdQuality.level === 'excellent' ? 'bg-emerald-500' :
                      rssdQuality.level === 'good' ? 'bg-blue-500' :
                      rssdQuality.level === 'fair' ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${hasFailed ? 0 : rssdQuality.percentage}%` }}
                  />
                </div>
                <p className={`text-sm font-medium ${hasFailed ? 'text-gray-600' : rssdQuality.color}`}>
                  {hasFailed
                    ? 'Check channel configuration'
                    : rssdQuality.level === 'excellent' ? 'Spend matches effects well' :
                      rssdQuality.level === 'good' ? 'Reasonable spend-effect alignment' :
                      rssdQuality.level === 'fair' ? 'Some misalignment detected' : 'Review channel contributions'}
                </p>
              </div>
            );
          })()}
        </div>

        {/* What Do These Numbers Mean? */}
        <details className="mt-6 group">
          <summary className="p-4 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors list-none">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-slate-600" />
                <span className="font-semibold text-slate-900">What do these numbers mean?</span>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-500 group-open:rotate-180 transition-transform" />
            </div>
          </summary>
          <div className="mt-2 p-5 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-lg border border-slate-100">
                <h5 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs flex items-center justify-center font-bold">1</span>
                  Prediction Error (NRMSE)
                </h5>
                <p className="text-sm text-slate-600">
                  How far off are the model's predictions? Think of it like a weather forecast—lower error means more reliable forecasts.
                  <span className="block mt-2 text-xs text-slate-500">Under 10% = excellent, 10-15% = good, 15-25% = fair, over 25% = needs work</span>
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-slate-100">
                <h5 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-bold">2</span>
                  Model Fit (R²)
                </h5>
                <p className="text-sm text-slate-600">
                  What percentage of your revenue ups and downs does the model explain? Higher is better—85%+ means the model captures most of what's happening.
                  <span className="block mt-2 text-xs text-slate-500">Over 85% = excellent, 70-85% = good, 50-70% = fair, under 50% = needs work</span>
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-slate-100">
                <h5 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs flex items-center justify-center font-bold">3</span>
                  Adjusted Model Fit
                </h5>
                <p className="text-sm text-slate-600">
                  Same as Model Fit, but penalizes adding too many channels. Useful when comparing models with different numbers of channels.
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-slate-100">
                <h5 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-rose-100 text-rose-700 text-xs flex items-center justify-center font-bold">4</span>
                  Business Alignment
                </h5>
                <p className="text-sm text-slate-600">
                  Do the channels where you spend more actually show more effect? Lower percentages mean spending patterns and detected effects line up well.
                  <span className="block mt-2 text-xs text-slate-500">Under 10% = excellent, 10-20% = good, 20-40% = fair, over 40% = needs review</span>
                </p>
              </div>
            </div>
          </div>
        </details>

        <div className="mt-6 p-4 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors duration-200"
          onClick={() => openChatWithQuestion('How do my train/test metrics compare?')}
          title="Click to ask the analyst"
        >
          <h3 className="font-semibold text-gray-900 mb-3">Model Performance Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">NRMSE</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Training:</span>
                  <span className="font-semibold">{results.metrics.nrmse_train !== undefined ? (results.metrics.nrmse_train * 100).toFixed(2) : 'N/A'}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Validation:</span>
                  <span className="font-semibold">{results.metrics.nrmse_val !== undefined ? (results.metrics.nrmse_val * 100).toFixed(2) : 'N/A'}%</span>
                </div>
                {results.metrics.nrmse_test !== undefined && results.metrics.nrmse_test !== 0 && (
                  <div className="flex justify-between text-blue-800 bg-blue-50 px-1 rounded">
                    <span className="font-medium">Test Set:</span>
                    <span className="font-bold">{(results.metrics.nrmse_test * 100).toFixed(2)}%</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">R²</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Training:</span>
                  <span className="font-semibold">{results.metrics.r2_train !== undefined ? (results.metrics.r2_train).toFixed(3) : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Validation:</span>
                  <span className="font-semibold">{results.metrics.r2_val !== undefined ? (results.metrics.r2_val).toFixed(3) : 'N/A'}</span>
                </div>
                {results.metrics.r2_test !== undefined && results.metrics.r2_test !== 0 && (
                  <div className="flex justify-between text-blue-800 bg-blue-50 px-1 rounded">
                    <span className="font-medium">Test Set:</span>
                    <span className="font-bold">{results.metrics.r2_test.toFixed(3)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Intelligent Diagnostics Section */}
      {diagnostics && (
        <div className="card p-0 overflow-hidden border-2 border-indigo-100 shadow-lg fade-in">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-8 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Intelligent Diagnostic Assistant
            </h2>
            <div className="text-indigo-100 text-sm font-medium">
              Powered by Qistrel Diagnostics Engine v1.0
            </div>
          </div>
          <div className="p-8">
            <DiagnosticAssistantPanel
              failureMode={diagnostics.failure_mode}
              issues={diagnostics.issues}
              recommendations={diagnostics.actionable_recommendations}
              qualityScore={diagnostics.quality_score}
              onApplyFix={(fixAction) => {
                // For post-training, some fixes might require re-training
                // For now, we just guide the user
                console.log("Guiding user for post-training fix:", fixAction);
                alert(`Recommended action: ${fixAction}. Please update your configuration and re-train for best results.`);
              }}
            />
          </div>
        </div>
      )}

      {/* Model Configuration - Collapsible */}
      <div className="card overflow-hidden">
        <button
          type="button"
          onClick={() => setShowModelConfig(!showModelConfig)}
          className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-gray-600" />
            Model Configuration
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {results.config?.media_channels?.length || 0} channels, {results.config?.control_vars?.length || 0} controls
            </span>
            {showModelConfig ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </button>

        {showModelConfig && (
          <div className="px-8 pb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Calendar Settings */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-lg border border-purple-200 cursor-pointer hover:shadow-lg transition-shadow duration-200"
                onClick={() => openChatWithQuestion('Explain the calendar settings and trend/seasonality to me in simple terms.')}
                title="Click to ask about calendar settings"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  Calendar Settings
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Target Variable:</span>
                    <span className="text-gray-900 font-semibold">{results.config?.dep_var || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Frequency:</span>
                    <span className="text-gray-900 font-semibold">{results.config?.frequency || 'W'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Trend Included:</span>
                    <span className={`font - semibold ${results.config?.calendar?.use_trend ? 'text-green-600' : 'text-gray-600'} `}>
                      <span className="flex items-center gap-1">
                        {results.config?.calendar?.use_trend ? <><Check className="w-4 h-4" /> Yes</> : <><X className="w-4 h-4" /> No</>}
                      </span>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Seasonality:</span>
                    <span className={`font - semibold ${results.config?.calendar?.use_seasonality ? 'text-green-600' : 'text-gray-600'} `}>
                      <span className="flex items-center gap-1">
                        {results.config?.calendar?.use_seasonality ? <><Check className="w-4 h-4" /> Yes</> : <><X className="w-4 h-4" /> No</>}
                      </span>
                    </span>
                  </div>
                  {results.config?.calendar?.seasonality_periods && (
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">Seasonality Periods:</span>
                      <span className="text-gray-900 font-semibold">{results.config.calendar.seasonality_periods.join(', ')}</span>
                    </div>
                  )}
                </div>
                <div className="mt-4 p-3 bg-purple-100 rounded text-xs text-purple-800 border border-purple-300">
                  <strong>What this means:</strong> These settings remove long-term trends and seasonal patterns from your revenue, isolating the true impact of marketing media. Calendar features help the model distinguish between seasonal spikes (holidays) and actual media-driven growth.
                </div>
              </div>

              {/* Train/Test Split */}
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-lg border border-indigo-100 cursor-pointer hover:shadow-lg transition-shadow duration-200"
                onClick={() => openChatWithQuestion('What control variables were used?')}
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-indigo-600" />
                  Variables
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Split Type:</span>
                    <span className="text-gray-900 font-semibold">{results.config?.train_test_split?.type || 'time'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Training Ratio:</span>
                    <span className="text-gray-900 font-semibold">
                      {(((results.config?.train_test_split?.train_ratio || 0.8) * 100).toFixed(0))}%
                    </span>
                  </div>
                  {results.config?.train_test_split?.val_ratio && (
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">Validation Ratio:</span>
                      <span className="text-gray-900 font-semibold">
                        {results.config.train_test_split.val_ratio !== undefined ? ((results.config.train_test_split.val_ratio * 100).toFixed(0)) : 'N/A'}%
                      </span>
                    </div>
                  )}
                  {results.config?.train_test_split?.test_ratio && (
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">Test Ratio:</span>
                      <span className="text-gray-900 font-semibold">
                        {results.config.train_test_split.test_ratio !== undefined ? ((results.config.train_test_split.test_ratio * 100).toFixed(0)) : 'N/A'}%
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-green-200">
                    <span className="text-gray-700 font-medium">Total Observations:</span>
                    <span className="text-gray-900 font-semibold">{results.metrics.n_obs}</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-green-100 rounded text-xs text-green-800 border border-green-300">
                  <strong>What this means:</strong> Your data was split into training data (used to build the model) and validation data (used to test it). This prevents overfitting and shows how well the model generalizes to new data.
                </div>
              </div>
            </div>

            {/* Media Channels Summary */}
            {results.config?.media_channels && results.config.media_channels.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-blue-600" />
                  Media Channels
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {results.config.media_channels.map((channel) => (
                    <div
                      key={channel.name}
                      className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded border-2 border-blue-300 cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200"
                      onClick={() => openChatWithQuestion(`Tell me about the ${channel.name} channel performance and what the numbers mean.`)}
                      title="Click to ask about this channel"
                    >
                      <p className="font-bold text-blue-900 mb-3 text-lg">{channel.name}</p>
                      <div className="text-sm text-gray-700 space-y-2">
                        <div className="flex justify-between items-start">
                          <span className="font-semibold text-gray-800 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Spend:</span>
                          <code className="bg-white px-2 py-1 rounded font-mono text-blue-600">{channel.column}</code>
                        </div>
                        {channel.variable_column ? (
                          <div className="flex justify-between items-start">
                            <span className="font-semibold text-gray-800 flex items-center gap-1"><LineChart className="w-3 h-3" /> Impressions:</span>
                            <code className="bg-white px-2 py-1 rounded font-mono text-green-600">{channel.variable_column}</code>
                          </div>
                        ) : (
                          <div className="flex justify-between items-start">
                            <span className="font-semibold text-gray-800 flex items-center gap-1"><LineChart className="w-3 h-3" /> Impressions:</span>
                            <span className="text-gray-500 italic">(same as spend)</span>
                          </div>
                        )}
                        <div className="flex justify-between items-start pt-2 border-t border-blue-200">
                          <span className="font-semibold text-gray-800 flex items-center gap-1"><Zap className="w-3 h-3" /> Transforms:</span>
                          <div className="text-right">
                            <p className="text-xs">{channel.adstock || 'geometric'} adstock</p>
                            <p className="text-xs">{channel.saturation || 'hill'} saturation</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-4 p-3 bg-gray-50 rounded flex items-start gap-2">
                  <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Note:</strong> Spend column maps to paid_media_spends. Impressions/delivery column maps to paid_media_vars. Adstock and saturation transformations apply to the impressions column.</span>
                </p>
              </div>
            )}

            {/* Control Variables Summary */}
            {results.config?.control_vars && results.config.control_vars.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-orange-600" />
                  Control Variables
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {results.config.control_vars.map((control) => (
                    <div key={control.name} className="bg-orange-50 p-3 rounded border border-orange-200">
                      <p className="font-bold text-orange-900">{control.name}</p>
                      <p className="text-xs text-gray-700"><span className="font-medium">Column:</span> {control.column}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Channel Contributions */}
      <div className="card p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="w-6 h-6 text-indigo-600" />
            Channel Contributions & Impact
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => openChatWithQuestion('Which media channels are performing best? Which should I spend more on?')}
              className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-900 px-3 py-1 rounded-full transition-colors"
              title="Ask about channel performance"
            >
              <Lightbulb className="w-3 h-3 inline mr-1" /> Best Channels
            </button>
            <button
              onClick={() => openChatWithQuestion('Explain the coefficient and impact values in simple terms.')}
              className="text-xs bg-green-100 hover:bg-green-200 text-green-900 px-3 py-1 rounded-full transition-colors"
              title="Ask about metrics"
            >
              <BookOpen className="w-3 h-3 inline mr-1" /> Explain Metrics
            </button>
          </div>
        </div>

        {/* Helper Language for Coefficients and Impact */}
        <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Understanding the Results
          </h4>
          <ul className="text-sm text-purple-800 space-y-2">
            <li><strong>Coefficient:</strong> The estimated effect size in the model (depends on feature scale). Media channels typically show positive coefficients (more spend = more revenue). Control variables may show negative coefficients (higher prices reduce demand).</li>
            <li><strong>Impact:</strong> How much this feature affects revenue relative to others. Positive = increases revenue, negative = decreases revenue. Use this to compare which channels matter most.</li>
            <li><strong>Media Channels:</strong> facebook_spend, google_spend, etc. show how much each dollar spent contributes to revenue.</li>
            <li><strong>Calendar Features:</strong> "trend" captures long-term growth; "sin_52" and "cos_52" capture yearly seasonal patterns (e.g., holiday effects).</li>
            <li><strong>Negative Values:</strong> Don't always mean bad! Control variables (price_index, promotions) often have negative coefficients (as they increase, revenue might decrease due to other factors).</li>
          </ul>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-300 bg-gray-50">
                <th className="px-4 py-3 text-left font-semibold text-gray-900">Feature</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-900">Coefficient</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-900">Impact</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(results.contributions).map(([name, contrib]) => (
                <tr
                  key={name}
                  className="border-b border-gray-200 hover:bg-blue-50 cursor-pointer transition-colors duration-200"
                  onClick={() => openChatWithQuestion(`Explain the ${name} coefficient and impact values to me.What do they mean for my business ? `)}
                  title="Click to ask about this feature"
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{name}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font - semibold ${contrib.coefficient !== undefined && contrib.coefficient >= 0 ? 'text-green-600' : 'text-red-600'} `}>
                      {contrib.coefficient !== undefined ? contrib.coefficient.toFixed(4) : 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font - semibold ${contrib.elasticity !== undefined && contrib.elasticity >= 0 ? 'text-green-600' : 'text-red-600'} `}>
                      {contrib.elasticity !== undefined ? contrib.elasticity.toFixed(4) : 'N/A'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Visualization Dashboard */}
      <VisualizationDashboard
        modelResults={visualizationData || results}
        title="Channel Contributions & Impact Analysis"
      />

      {/* Advanced / Technical Details (Collapsible) */}
      <details className="card p-4 border border-gray-200 bg-gray-50">
        <summary className="font-semibold text-gray-700 cursor-pointer flex items-center gap-2">
          <Wrench className="w-4 h-4" />
          <span>Advanced Technical Details</span>
        </summary>

        <div className="mt-6 space-y-6">
          {/* Raw Coefficients */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-gray-600" />
              Model Coefficients
            </h3>
            <div className="bg-white p-4 rounded-lg max-h-60 overflow-y-auto border border-gray-200">
              <pre className="text-xs font-mono text-gray-700">
                {JSON.stringify(results.coefficients, null, 2)}
              </pre>
            </div>
          </div>

          {/* Optimization Parameters */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-600" />
              Optimized Hyperparameters
            </h3>
            <div className="bg-white p-4 rounded-lg max-h-60 overflow-y-auto border border-gray-200">
              <pre className="text-xs font-mono text-gray-700">
                {JSON.stringify(results.best_params, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </details>

      {/* Interpretation Guide with Quick Actions */}
      <div className="card p-8 border-2 border-blue-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" />
            How to Interpret Results
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => openChatWithQuestion('Explain all these results to me like I\'m 12 years old.')}
              className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-900 px-3 py-1 rounded-full transition-colors"
              title="Simple explanation"
            >
              <Baby className="w-3 h-3 inline mr-1" /> ELI12
            </button>
            <button
              onClick={() => openChatWithQuestion('What should I do next with these results? Give me actionable recommendations.')}
              className="text-xs bg-orange-100 hover:bg-orange-200 text-orange-900 px-3 py-1 rounded-full transition-colors"
              title="Get recommendations"
            >
              <Target className="w-3 h-3 inline mr-1" /> Next Steps
            </button>
          </div>
        </div>
        <div className="space-y-4 text-sm">
          <div>
            <h3 className="font-semibold text-gray-900">NRMSE (Normalized RMSE)</h3>
            <p className="text-gray-700 mt-1">
              Measures prediction accuracy. Lower is better. Range: 0-1 (typically 0.05-0.2 is good).
              Calculated as: RMSE / (max(y) - min(y))
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">R² (Coefficient of Determination)</h3>
            <p className="text-gray-700 mt-1">
              Proportion of variance explained. Range: -∞ to 1. Higher is better. 0.8+ indicates good fit.
              Interpretation: {results.metrics.r2_val !== undefined ? (results.metrics.r2_val * 100).toFixed(0) : 'N/A'}% of target variance explained.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Coefficients</h3>
            <p className="text-gray-700 mt-1">
              Linear regression coefficients for each feature. Positive = increases target.
              Negative = decreases target. Magnitude indicates strength of effect.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Train vs Validation Gap</h3>
            <p className="text-gray-700 mt-1">
              If validation metrics significantly worse than training → possible overfitting.
              A small gap (±5%) indicates good generalization.
            </p>
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className="card p-8 border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-blue-50">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Download className="w-6 h-6 text-purple-600" />
          Export Your Results
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              const dataStr = JSON.stringify(results, null, 2);
              const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
              const exportFileDefaultName = `mmm_results_${jobId}.json`;
              const linkElement = document.createElement('a');
              linkElement.setAttribute('href', dataUri);
              linkElement.setAttribute('download', exportFileDefaultName);
              linkElement.click();
            }}
            className="btn-primary justify-center text-base"
          >
            <FileJson className="w-4 h-4 inline mr-1" /> Download JSON
          </button>
          <button
            onClick={() => {
              const csv = [
                ['Metric', 'Value'],
                ['NRMSE (Train)', results.metrics.nrmse_train],
                ['NRMSE (Validation)', results.metrics.nrmse_val],
                ['R² (Train)', results.metrics.r2_train],
                ['R² (Validation)', results.metrics.r2_val],
                ['Adjusted R² (Train)', results.metrics.adj_r2_train],
                ['Adjusted R² (Validation)', results.metrics.adj_r2_val],
                ['N Observations', results.metrics.n_obs],
                ['N Parameters', results.metrics.n_params],
              ]
                .map(row => row.join(','))
                .join('\n');
              const dataUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
              const exportFileDefaultName = `mmm_metrics_${jobId}.csv`;
              const linkElement = document.createElement('a');
              linkElement.setAttribute('href', dataUri);
              linkElement.setAttribute('download', exportFileDefaultName);
              linkElement.click();
            }}
            className="btn-success justify-center text-base"
          >
            <FileSpreadsheet className="w-4 h-4 inline mr-1" /> Download CSV
          </button>
        </div>
      </div>

      {/* Saved Models Section */}
      <div className="card p-8 border-2 border-dashed border-green-300 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-6 h-6 text-green-600" />
            Saved Models
          </h2>
          <button
            onClick={() => setShowSavedModels(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <FolderOpen className="w-4 h-4 inline mr-1" /> Browse Saved Models
          </button>
        </div>
        <p className="text-gray-700 mb-4">
          This model was automatically saved when the analysis completed. You can browse and compare all your previously saved models to find the best fit for your marketing strategy.
        </p>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            <strong>Current Model:</strong> Auto-saved at {new Date().toLocaleString()}
          </p>
          <p className="text-sm text-gray-600 mt-2">
            <strong>Key Metrics:</strong> NRMSE: {results?.metrics.nrmse_val !== undefined ? results.metrics.nrmse_val.toFixed(4) : 'N/A'} | R²: {results?.metrics.r2_val !== undefined ? results.metrics.r2_val.toFixed(4) : 'N/A'}
          </p>
        </div>
      </div>

      {/* Saved Models Browser Modal */}
      {showSavedModels && (
        <SavedModelsBrowser
          onSelectModel={handleSelectModel}
          onClose={() => setShowSavedModels(false)}
        />
      )}

      {/* Settings Modal */}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}

      {/* Scenario Planner Modal */}
      {showScenarioPlanner && results && (
        <ScenarioPlannerModal
          modelId={jobId}
          channels={results.config?.media_channels || []}
          totalBudget={undefined}
          depVarName={results.config?.dep_var_label || results.config?.dep_var || 'Revenue'}
          onClose={() => setShowScenarioPlanner(false)}
        />
      )}

      {/* Chat Panel (Clippy-style) */}
      {results && (
        <ChatPanel
          modelResults={results}
          savedModels={savedModels}
          isOpen={chatOpen}
          onToggle={() => setChatOpen(!chatOpen)}
          initialMessage={chatQuestion}
        />
      )}
    </div>
  );
};
